import { useCallback, useEffect, useMemo, useState } from "react";
import dropdownService from "../services/api/service/dropdownService";
import eligibilityCheckServices from "../services/api/service/eligibilityCheckService";

type Option = {
  value: string;
  label: string;
  countryId?: string;
  nationalityId?: string;
  data?: any;
};

const toOption = (item: any): Option => ({
  value: String(
    item?.value ??
      item?.code ??
      // item?.id ??
      item?.nationalityCode ??
      item?.nationalityDesc ??
      item?.maritalStatusCode ??
      item?.maritalStatusName ??
      item?.maritalStatusDesc ??
      item?.label ??
      item?.name ??
      item ??
      ""
  ),
  label: String(
    item?.label ??
      item?.name ??
      item?.desc ??
      item?.nationalityDesc ??
      item?.maritalStatusName ??
      item?.maritalStatusDesc ??
      item?.value ??
      item?.code ??
      item ??
      ""
  ),
  nationalityId: String(item?.id ?? item?.value ?? item?.code ?? ""),
});

/** Cities-by-country-state API: `{ city, cityName }` (and common alternates). */
const toCityOption = (item: any): Option => ({
  value: String(
    item?.city ??
      item?.cityCode ??
      item?.value ??
      item?.code ??
      ""
  ),
  label: String(
    item?.cityName ??
      item?.cityDesc ??
      item?.name ??
      item?.label ??
      item?.value ??
      ""
  ),
});

const uniqueByValue = (options: Option[]) => {
  const map = new Map<string, Option>();
  options.forEach((opt) => {
    const value = String(opt.value ?? "").trim().toLowerCase();
    const label = String(opt.label ?? "").trim().toLowerCase();
    // Key on the value+label combination, not value alone: two different
    // entries that happen to share a code (a master-data duplicate) must
    // not collapse into one just because their codes match, and an entry
    // whose code resolved to "" (but has a real label) must not be dropped
    // just because an empty string is falsy.
    const key = value || label;
    if (!key) return;
    map.set(`${value}::${label}`, opt);
  });
  return Array.from(map.values());
};

export function useApplicantMasterData() {
  const [countryOptions, setCountryOptions] = useState<Option[]>([]);
  const [nationalityOptions, setNationalityOptions] = useState<Option[]>([]);
  const [maritalStatusOptions, setMaritalStatusOptions] = useState<Option[]>([]);
  const [cityOptionsByCountry, setCityOptionsByCountry] = useState<
    Record<string, Option[]>
  >({});
  const [loading, setLoading] = useState(false);


  useEffect(() => {
    let active = true;
    const loadMasters = async () => {
      setLoading(true);
      try {
        const [countriesResp, nationalityResp, maritalResp] = await Promise.all([
          dropdownService.getCountries(),
          dropdownService.getNationalityList(),
          dropdownService.getMaritalStatusMaster(),
        ]);

        if (!active) return;

        const countries = uniqueByValue(
          ((countriesResp?.data ?? []) as any[]).map((item) => ({
            value: String(item?.countryDesc ?? item?.countryId ?? ""),
            label: String(item?.countryDesc ?? item?.countryId ?? ""),
            countryId: String(item?.countryId ?? ""),
          }))
        );
        setCountryOptions(countries);

        const nationalities = uniqueByValue(
          ((nationalityResp as { data?: any })?.data ?? []).map(toOption)
        );
        setNationalityOptions(nationalities);

        const marital = uniqueByValue(
          ((maritalResp?.data ?? []) as any[]).map(toOption)
        );
        setMaritalStatusOptions(marital);
      } catch {
        if (!active) return;
        setCountryOptions([]);
        setNationalityOptions([]);
        setMaritalStatusOptions([]);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadMasters();
    return () => {
      active = false;
    };
  }, []);

  const fetchCitiesByCountryState = useCallback(
    async (countryName: string, stateName?: string): Promise<Option[]> => {
      const countryKey = String(countryName || "").trim();
      if (!countryKey) return [];
      const request = (async () => {
        const resp = await eligibilityCheckServices.getCitiesByCountryState(
          countryKey,
          stateName
        );
        const payload = resp?.data?.data ?? resp?.data ?? resp ?? [];
        const rawCities = Array.isArray(payload)
          ? payload
          : typeof payload === "object" && payload !== null
            ? Object.values(payload)
            : [];

        const cityOptions = uniqueByValue(
          rawCities
            .map(toCityOption)
            .filter((opt) => opt.value || opt.label),
        );
        setCityOptionsByCountry((prev) => ({ ...prev, [countryKey]: cityOptions }));
        return cityOptions;
      })().catch(() => {
        setCityOptionsByCountry((prev) => ({ ...prev, [countryKey]: [] }));
        return [];
      });
      return request;
    },
    []
  );

  const getCityOptionsForCountry = useCallback(
    (countryName: string): Option[] => {
      const countryKey = String(countryName || "").trim();
      return cityOptionsByCountry[countryKey] || [];
    },
    [cityOptionsByCountry]
  );

  return useMemo(
    () => ({
      loading,
      countryOptions,
      nationalityOptions,
      maritalStatusOptions,
      fetchCitiesByCountryState,
      getCityOptionsForCountry,
    }),
    [
      loading,
      countryOptions,
      nationalityOptions,
      maritalStatusOptions,
      fetchCitiesByCountryState,
      getCityOptionsForCountry,
    ]
  );
}